import type { CustomerProperties } from "@api/customer";
import type {
  AutomationConditionOperator,
  Customer,
  ListCondition,
  ListConditionOperator,
  ListConditionOperatorOp,
  ListConditionOperatorValue,
  ListEvent,
  ListRule,
  PropertyType,
  Subscription,
  SubscriptionPlan,
} from "@api/types";
import type { HslColor } from "@hello-pangea/color-picker";
import { type ClassValue, clsx } from "clsx";
import { capitalize, isArray, kebabCase, lastIndexOf, omit, startCase, transform } from "lodash";
import isPlainObject from "lodash/isPlainObject";
import { DateTime, Duration } from "luxon";
import type { SortingRule } from "react-table";
import { default as _slugify } from "slugify";
import { SegmentedMessage } from "sms-segments-calculator";
import { twMerge } from "tailwind-merge";
import { customerPropertyOptions, ORDINAL_PROPERTIES, STRING_OPERATORS } from "./constants/lists";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_APP_URL}${path}`;
}

export const complianceMessage = "Reply STOP to optout";

export function withComplianceMessage(input: string) {
  if (includesComplianceMessage(input)) {
    return input;
  }
  return `${input}\n\n${complianceMessage}`;
}

export function includesComplianceMessage(input: string) {
  return !!input.match(/\breply stop\b/gi);
}

export const isServer = typeof window === "undefined";

export function slugify(input: string) {
  return _slugify(input, {
    lower: true,
    trim: true,
  });
}

export function pluralize(noun: string, count: number): string {
  if (count === 1) return noun;
  if (lastIndexOf(noun, "y") === noun.length - 1) {
    return `${noun.substring(0, noun.length - 1)}ies`;
  }
  return `${noun}s`;
}

/**
 * Truncates an email address while preserving the domain.
 * Useful for displaying long email addresses in constrained UI spaces.
 *
 * @param email - The email address to truncate
 * @param maxLocalLength - Maximum characters to keep from local part (default: 8)
 * @returns Truncated email with format "local...@domain.com"
 *
 * @example
 * truncateEmail("verylongemail@example.com") // "verylong...@example.com"
 * truncateEmail("short@example.com") // "short@example.com" (no truncation needed)
 */
export function truncateEmail(email: string, maxLocalLength: number = 15): string {
  const atIndex = email.indexOf("@");
  if (atIndex === -1) return email;

  const localPart = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  if (localPart.length <= maxLocalLength) {
    return email;
  }

  const truncatedLocal = `${localPart.substring(0, maxLocalLength)}...`;
  return truncatedLocal + domain;
}

export function calculateSegments(text: string): number {
  const message = new SegmentedMessage(text, "auto", true);
  return message.segmentsCount;
}

export function toFixed(value: number, fractionDigits: number): string {
  return value.toFixed(fractionDigits).replace(/\.?0+$/gi, "");
}

export function formatNumber(
  value?: number | string | null,
  options: { precision?: number } = { precision: 0 },
): string {
  const fixedNumber = Number(Number(value ?? 0).toFixed(options.precision));
  return fixedNumber.toLocaleString();
}

export function formatPercent(
  value?: number,
  options: { precision?: number } = { precision: 2 },
): string {
  if (!value) return "0%";
  const fixedNumber = Number(Number(value * 100).toFixed(options.precision));
  return `${fixedNumber.toLocaleString()}%`;
}

export function formatMessageQuota(value?: number | string | null): string {
  if (typeof value === "number" && value >= 1000000) {
    return "Unlimited";
  } else {
    return Number(value ?? 0).toLocaleString();
  }
}

export function formatDate(
  value: Date | string,
  format?: string,
  options?: { zone?: string },
): string {
  const date = new Date(value);
  return DateTime.fromJSDate(date, options).toFormat(format || "ff");
}

/**
 * Format time only
 */
export function formatTime(datetime: string | null | undefined): string {
  if (!datetime) return "—";

  const d = new Date(datetime);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDurationRelative(totalSeconds: number): string {
  const dur = Duration.fromObject({ seconds: totalSeconds }).shiftTo("hours", "minutes", "seconds");
  const h = Math.floor(dur.hours);
  const m = Math.floor(dur.minutes);
  const s = Math.floor(dur.seconds);
  if (h > 0) {
    return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
  }
  if (m >= 5) {
    return `${s >= 30 ? m + 1 : m} minutes`;
  }
  if (m >= 1) {
    return s > 0 ? `${m} min ${s} sec` : `${m} min`;
  }
  return `${s} seconds`;
}

/**
 * Format time range
 */
export function formatTimeRange(start: string | undefined, end: string | undefined): string {
  if (!start) return "—";
  const startTime = formatTime(start);
  const endTime = end ? formatTime(end) : null;
  return endTime ? `${startTime} - ${endTime}` : startTime;
}

export function flattenObject(obj: Record<string, string>, prefix = ""): Record<string, any> {
  return transform(
    obj,
    (result: Record<string, any>, value: any, key: string) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (isPlainObject(value)) {
        Object.assign(result, flattenObject(value, newKey));
      } else if (isArray(value)) {
        value.forEach((item, index) => {
          Object.assign(result, flattenObject(item, `${newKey}[${index}]`));
        });
      } else {
        if (value !== "" && value !== null && value !== undefined) {
          result[newKey] = `${value}`;
        }
      }
    },
    {},
  );
}

export async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type NullishStrNum = string | number | null | undefined;

export function parseNumber(defaultVal?: NullishStrNum) {
  return (val: string): NullishStrNum => {
    return val === "" ? defaultVal : val?.replace(/[^0-9.]/g, "");
  };
}

export function sanitizePhoneNumber(num: string) {
  return num?.replace(/[^0-9]/g, "");
}

export function isValidPhoneNumber(num: string) {
  const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$/im;
  return phoneRegex.test(num);
}

export function formatPhoneNumber(
  num: string,
  options: { international: boolean } = { international: false },
) {
  const cleaned = `${num}`.replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [
      options.international ? intlCode : "",
      "(",
      match[2],
      ") ",
      match[3],
      "-",
      match[4],
    ].join("");
  }
  return num;
}

export function getInitials(string: string) {
  const names = string.split(" ");
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
}

/**
 *
 * @param {Number} num
 * @returns {String} 1K, 1M, 1B
 */
export const abbreviateNumberByMagnitude = (num: number, { fixed }: any = {}): string => {
  const fix = (val: number) => (fixed ? val.toFixed(fixed) : val);
  if (num >= 1000000000000) {
    return `${fix(num / 1000000000000)}T`;
  } else if (num >= 1000000000) {
    return `${fix(num / 1000000000)}B`;
  } else if (num >= 1000000) {
    return `${fix(num / 1000000)}M`;
  } else if (num >= 1000) {
    return `${fix(num / 1000)}K`;
  } else {
    return `${num}`;
  }
};

export function buildQueryString({
  globalFilter,
  filters,
  sortBy,
  page,
}: {
  globalFilter?: string;
  filters?: any;
  sortBy?: SortingRule<object>[];
  page?: number;
}) {
  const params = new URLSearchParams();
  if (globalFilter) {
    params.set("query", globalFilter);
  }
  if (filters && filters.length > 0) {
    params.set("filters", JSON.stringify(filters));
  }
  if (sortBy && sortBy.length > 0) {
    const sortString = sortBy.map(({ id, desc }) => `${id}:${desc ? "desc" : "asc"}`).join(",");
    params.set("sort", sortString);
  }
  if (page) {
    params.set("page", String(page));
  }
  return params.toString();
}

export function buildSortArray(sortByParam: string | string[]): SortingRule<object>[] {
  const sortByArray = Array.isArray(sortByParam) ? sortByParam : [sortByParam];

  return sortByArray.map((s: string) => {
    const [id, order] = s.split(":");
    return { id, desc: order === "desc" };
  });
}

export function getCustomerName(customer: Customer): string {
  if (!customer) return "Unknown";
  if (!customer.firstName && !customer.lastName) {
    return customer.phoneNumber ? formatPhoneNumber(customer.phoneNumber) : "Unknown";
  }
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return fullName ? startCase(capitalize(fullName)) : "Unknown";
}

export function getCustomerAvatarSrc(customer: Customer): string | undefined {
  const source = getCustomerSource(customer);
  const props = customer?.properties;

  const avatarPaths: Record<string, string | undefined> = {
    mindbody: props?.mindbody?.PhotoUrl,
    tablelist: props?.tablelist?.profileImage,
    "mariana-tek": props?.mariana_tek?.profile_image,
    fitdegree:
      props?.fitdegree?.profile_image?.sources?.[
        props?.fitdegree?.profile_image?.sources?.length - 1
      ]?.url,
  };

  return avatarPaths[source];
}

export function getCustomerSource(customer: Customer): string {
  return customer?.source ? kebabCase(customer.source) : "helios";
}

export function booleanToYesNo(boolean: boolean): string {
  return boolean ? "Yes" : "No";
}

/**
 * Format a USD amount for display. Missing or non-finite amounts (undefined,
 * null, NaN) render as "$0.00" so a plan or order that has not loaded yet can
 * never throw. String amounts are returned as-is because some integration
 * payloads (e.g. Mariana Tek order lines) already carry pre-formatted values.
 */
export function formatCurrency(amount: string | number | null | undefined): string {
  if (typeof amount === "string") return amount;
  const value = typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function formatCurrencyCompact(amount: number): string {
  const abs = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (abs >= 1_000_000)
    return `${sign}$${(abs / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1)}K`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(abs % 100 === 0 ? 1 : 2)}K`;
  if (abs === Math.floor(abs)) return `${sign}$${abs}`;
  return `${sign}$${abs.toFixed(2)}`;
}

/**
 * Segment rules can be persisted either as a flat `ListRule[]` (a single AND
 * group — the legacy shape) or as a nested `ListRule[][]` (OR-of-AND groups,
 * where each inner array is AND'd and the groups are OR'd). The grouped shape
 * is detected by the first element being an array.
 *
 * Mirrors the backend `isRuleGroups` in `apps/api/src/modules/list/model.ts`.
 */
export function isRuleGroups(rules: unknown): rules is any[][] {
  return Array.isArray(rules) && rules.length > 0 && Array.isArray(rules[0]);
}

/**
 * Collapse either rules shape into a single flat list. Used by consumers that
 * don't care about the OR grouping (the legacy V1 editor, key-type collection)
 * so they keep working when handed grouped rules.
 */
export function flattenRuleGroups(rules: unknown): any[] {
  if (!Array.isArray(rules)) return [];
  return isRuleGroups(rules) ? rules.flat() : rules;
}

export function parseRulesForApi(rules: ListRule[]): any[] {
  return rules.map((r) => {
    const { type, rule } = r;
    if (type === "condition") {
      return {
        type: "condition",
        rule: parseConditionsForApi([rule])[0],
      };
    } else {
      return {
        type: "event",
        rule: parseEventsForApi(rule),
      };
    }
  });
}

/**
 * Convert the V2 editor's `groups` form state back into the API rules shape.
 * Empty groups are dropped. A single remaining group serializes to the flat
 * `ListRule[]` shape (backward compatible — existing segments stay flat); two
 * or more groups serialize to the nested `ListRule[][]` OR-of-AND shape.
 */
export function parseRuleGroupsForApi(groups: Array<{ rules: ListRule[] }> | undefined): any[] {
  const parsed = (groups ?? [])
    .map((group) => parseRulesForApi(group?.rules ?? []))
    .filter((group) => group.length > 0);
  if (parsed.length <= 1) return parsed[0] ?? [];
  return parsed;
}

export function parseEventsForApi(event: ListEvent): ListEvent {
  const { type, event: eventType, count, conditions } = event;
  const parsedConditions = parseConditionsForApi(conditions);
  const parsedCount = count ? parseCountForApi(count) : null;

  return {
    type,
    event: eventType,
    count: parsedCount,
    conditions: parsedConditions,
  };
}

export function parseCountForApi(count: ListConditionOperator): ListConditionOperator {
  const { op, value } = count;
  let parsedOp = op;
  // Empty / blank / non-numeric inputs default to "at least 1" so the
  // preview endpoint receives a usable integer rather than 0 / NaN / null.
  // Note: `Number('')` is 0 (not NaN), so we also short-circuit empties.
  const isBlank = value === undefined || value === null || value === "" || value === "NaN";
  const numeric = Number(value);
  let parsedValue = isBlank || !Number.isFinite(numeric) ? 1 : numeric;
  if (op === "$exists") {
    parsedOp = "$gte";
    parsedValue = isBlank || !Number.isFinite(numeric) ? 1 : numeric;
  }

  return { op: parsedOp, value: parsedValue };
}

export function parseConditionsForApi(conditions: ListCondition[]): ListCondition[] {
  return conditions.map((condition) => {
    const { type, operators } = condition;

    const coercedOperators: ListConditionOperator[] = [];

    operators.forEach((operator) => {
      let coercedOp: ListConditionOperatorOp | undefined;
      let coercedValue: ListConditionOperatorValue;

      // Existence operators are encoded by the V2 chip editor as a single
      // op that carries its own boolean — `$exists:true` ("is set") /
      // `$exists:false` ("is not set"). The API only accepts the bare
      // `$exists` op with a boolean value, so decode here regardless of the
      // field's type (existence applies to strings, numbers, dates, enums,
      // arrays, and booleans alike). Also normalizes the legacy bare
      // `$exists` shape that older editors persisted with a stringy value.
      if (
        operator.op === "$exists" ||
        operator.op === "$exists:true" ||
        operator.op === "$exists:false"
      ) {
        const exists =
          operator.op === "$exists:true"
            ? true
            : operator.op === "$exists:false"
              ? false
              : operator.value === true || operator.value === "true";
        coercedOperators.push({ op: "$exists", value: exists });
        return;
      }

      switch (type) {
        case "number":
          if (["$last", "$next"].includes(operator.op)) {
            coercedValue = handleOrdinalOperator(operator, coercedOperators);
            coercedOp =
              operator.op === "$last" ? "$gte" : operator.op === "$next" ? "$lte" : undefined;
          } else {
            coercedValue = Number(operator.value);
          }
          break;
        case "boolean":
          // Accept either the legacy stringy form ("true"/"false") that older
          // editor versions persisted, or a real boolean from the current
          // chip editor — both must round-trip to a real boolean so Mongo's
          // `$eq` matches against ingested boolean documents (e.g. Mariana
          // Tek's `is_free_class`).
          coercedValue = operator.value === true || operator.value === "true";
          break;
        // biome-ignore lint/suspicious/noFallthroughSwitchClause: non-relative dates use string operator coercion
        case "date":
          if (operator.op === "$exactLast" || operator.op === "$exactNext") {
            handleExactDateOperator(operator, coercedOperators);
            return;
          }
          if (["$last", "$next"].includes(operator.op)) {
            coercedValue = handleDateOperator(operator, coercedOperators);
            coercedOp =
              operator.op === "$last" ? "$gte" : operator.op === "$next" ? "$lte" : undefined;
            break;
          }
          if (
            (operator.op === "$lte" || operator.op === "$lt") &&
            operator.value?.$now?.duration_unit &&
            operator.value?.$now?.duration_value
          ) {
            const unit = String(operator.value.$now.duration_unit);
            const amount = Math.abs(Number(operator.value.$now.duration_value));
            coercedValue = { $now: { [unit]: Number.isFinite(amount) ? -amount : 0 } };
            break;
          }
        case "string":
          coercedValue = operator.value;
          break;
        // case 'enum':
        default:
          coercedValue = operator.value;
      }

      if (coercedOp || operator.op) {
        coercedOperators.push({
          op: coercedOp || operator.op,
          value: coercedValue,
        });
      }
    });

    return omit({ ...condition, operators: coercedOperators }, ["condition", "type"]);
  });
}

function handleExactDateOperator(
  operator: ListConditionOperator,
  coercedOperators: ListConditionOperator[],
): void {
  const unit = operator.value?.$now?.duration_unit;
  const amount = Number(operator.value?.$now?.duration_value);
  if (!unit || !Number.isFinite(amount) || amount <= 0) return;

  if (operator.op === "$exactLast") {
    coercedOperators.push(
      { op: "$gte", value: { $now: { [unit]: -(amount + 1) } } },
      { op: "$lt", value: { $now: { [unit]: -amount } } },
    );
    return;
  }

  if (operator.op === "$exactNext") {
    coercedOperators.push(
      { op: "$gte", value: { $now: { [unit]: amount } } },
      { op: "$lt", value: { $now: { [unit]: amount + 1 } } },
    );
  }
}

function handleDateOperator(
  operator: ListConditionOperator,
  coercedOperators: ListConditionOperator[],
): ListConditionOperatorValue {
  const coercedValue: any = { $now: {} };

  if (operator.value?.$now?.duration_unit && operator.value?.$now?.duration_value) {
    const durationValue = Number(operator.value.$now.duration_value);
    coercedValue.$now[operator.value.$now.duration_unit] =
      operator.op === "$last" ? durationValue * -1 : durationValue;

    if (operator.op === "$next") {
      // Add the second operator when the current operator is $next
      coercedOperators.push({ op: "$gte", value: { $now: { days: 0 } } });
    }
  }

  return coercedValue;
}

function handleOrdinalOperator(
  operator: ListConditionOperator,
  coercedOperators: ListConditionOperator[],
): ListConditionOperatorValue {
  let coercedValue: any = {};

  if (operator.op === "$next") {
    coercedValue = { $ordinal: Number(operator.value?.$lte?.$ordinal) };
    coercedOperators.push({ op: "$gt", value: { $ordinal: 0 } });
  }
  if (operator.op === "$last") {
    coercedValue = { $ordinal: Number(operator.value?.$gte?.$ordinal) * -1 };
    coercedOperators.push({ op: "$lt", value: { $ordinal: 0 } });
  }

  return coercedValue;
}

/**
 * Rebuild the `$exactLast` / `$exactNext` operator the editor shows from the
 * `$gte` + `$lt` window the API persists. Returns `null` when no pair matches.
 * Shared with the read-only chip view so both surfaces phrase the rule the
 * same way instead of listing the raw window bounds.
 */
function nowObject(value: ListConditionOperatorValue): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (!("$now" in value)) return null;
  const now = (value as { $now?: unknown }).$now;
  if (!now || typeof now !== "object" || Array.isArray(now)) return null;
  return now as Record<string, unknown>;
}

/**
 * Read a relative `$now` bound. Empty `{}` is "now" (0 days), matching
 * seeded intro windows that store `$gte { $now: {} }`.
 */
function nowUnitAmount(now: Record<string, unknown>): { unit: string; amount: number } | null {
  if (
    typeof now.duration_unit === "string" &&
    now.duration_value != null &&
    now.duration_value !== ""
  ) {
    const amount = Number(now.duration_value);
    if (!Number.isFinite(amount)) return null;
    return { unit: now.duration_unit, amount };
  }
  const numeric = Object.entries(now).find(
    ([key, amount]) => !key.startsWith("duration_") && typeof amount === "number",
  );
  if (numeric) return { unit: numeric[0], amount: numeric[1] as number };
  if (Object.keys(now).length === 0) return { unit: "days", amount: 0 };
  return null;
}

function operatorNowBound(
  operator: ListConditionOperator,
): { unit: string; amount: number } | null {
  const now = nowObject(operator.value);
  return now ? nowUnitAmount(now) : null;
}

export function collapseExactDateOperatorsForForm(
  operators: ListConditionOperator[],
): ListConditionOperator[] | null {
  for (let i = 0; i < operators.length; i++) {
    for (let j = i + 1; j < operators.length; j++) {
      const collapsed = matchExactDateOperatorPair(operators[i], operators[j]);
      if (collapsed) {
        const rest = operators.filter((_, idx) => idx !== i && idx !== j);
        return [collapsed, ...rest];
      }
    }
  }
  return null;
}

function matchExactDateOperatorPair(
  a: ListConditionOperator,
  b: ListConditionOperator,
): ListConditionOperator | null {
  const gte = a.op === "$gte" ? a : b.op === "$gte" ? b : null;
  const lt = a.op === "$lt" ? a : b.op === "$lt" ? b : null;
  if (!gte?.value || typeof gte.value !== "object" || !("$now" in gte.value)) return null;
  if (!lt?.value || typeof lt.value !== "object" || !("$now" in lt.value)) return null;

  const gteNow = gte.value.$now as Record<string, number>;
  const ltNow = lt.value.$now as Record<string, number>;
  const gteUnit = Object.keys(gteNow).find((key) => typeof gteNow[key] === "number");
  const ltUnit = Object.keys(ltNow).find((key) => typeof ltNow[key] === "number");
  if (!gteUnit || gteUnit !== ltUnit) return null;

  const gteVal = gteNow[gteUnit];
  const ltVal = ltNow[ltUnit];

  if (gteVal < 0 && ltVal < 0 && gteVal === ltVal - 1) {
    return {
      op: "$exactLast",
      value: {
        $now: { duration_unit: gteUnit, duration_value: String(Math.abs(ltVal)) },
      },
    };
  }

  if (gteVal > 0 && ltVal > 0 && ltVal === gteVal + 1) {
    return {
      op: "$exactNext",
      value: {
        $now: { duration_unit: gteUnit, duration_value: String(gteVal) },
      },
    };
  }

  return null;
}

export function parseRulesFromApi(
  rules: any[],
  customerProperties?: CustomerProperties,
): ListRule[] {
  // Defensive: a grouped (`ListRule[][]`) value flattens to a single AND list
  // so legacy callers (V1 editor) never crash on the new shape.
  return flattenRuleGroups(rules).map((r) => {
    const { type, rule } = r;
    if (type === "condition") {
      return {
        type: "condition",
        rule: parseConditionsFromApi([rule], customerProperties)[0],
      };
    } else {
      return {
        type: "event",
        rule: parseEventsFromApi(rule, customerProperties),
      };
    }
  });
}

/**
 * Parse persisted API rules into the V2 editor's `groups` form state. Accepts
 * either the flat shape (wrapped into a single group) or the nested grouped
 * shape. Always returns at least one group so the editor renders an empty
 * group to add criteria into.
 */
export function parseRuleGroupsFromApi(
  rules: any[],
  customerProperties?: CustomerProperties,
): Array<{ rules: ListRule[] }> {
  const groups = isRuleGroups(rules) ? rules : [Array.isArray(rules) ? rules : []];
  const parsed = groups.map((group) => ({
    rules: parseRulesFromApi(group, customerProperties),
  }));
  return parsed.length ? parsed : [{ rules: [] }];
}

/**
 * Infer the form-side PropertyType for a saved condition's operators when no
 * property metadata is available (event nested conditions or unsampled keys).
 * Used to drive operator UI selection and the round-trip back through
 * parseConditionsForApi.
 */
export function inferTypeFromOperators(operators: ListConditionOperator[]): PropertyType {
  const isDateValue = (value: unknown) =>
    !!value && typeof value === "object" && ("$now" in value || "$date" in value);
  // `$not` wraps a whole operator object (`{ $gte: { $now: {} } }`), so look one level in.
  const hasDateShape = operators.some(
    (op) =>
      isDateValue(op.value) ||
      (op.op === "$not" &&
        !!op.value &&
        typeof op.value === "object" &&
        Object.values(op.value as object).some(isDateValue)),
  );
  if (hasDateShape) return "date";
  const primitives = operators.map((op) => op.value).filter((v) => typeof v !== "object");
  if (primitives.length && primitives.every((v) => typeof v === "boolean")) {
    return "boolean";
  }
  if (primitives.length && primitives.every((v) => typeof v === "number" && Number.isFinite(v))) {
    return "number";
  }
  return "string";
}

/**
 * Collect every property key referenced by saved rules along with an inferred
 * type. Used to augment customerPropertyOptions so the form can render rules
 * whose keys aren't in the sampled customer-property set yet.
 */
export function collectRuleKeyTypes(rules: any[]): Array<{ key: string; type: PropertyType }> {
  const collected: Array<{ key: string; type: PropertyType }> = [];
  const seen = new Set<string>();
  function push(key: string, operators: ListConditionOperator[] | undefined) {
    if (!key || seen.has(key)) return;
    seen.add(key);
    collected.push({ key, type: inferTypeFromOperators(operators ?? []) });
  }
  for (const r of flattenRuleGroups(rules ?? [])) {
    if (!r) continue;
    if (r.type === "condition") {
      push(r.rule?.key, r.rule?.operators);
    } else if (r.type === "event") {
      for (const c of r.rule?.conditions ?? []) {
        push(c.key, c.operators);
      }
    }
  }
  return collected;
}

export function parseEventsFromApi(
  event: ListEvent,
  customerProperties?: CustomerProperties,
): ListEvent {
  const { type, event: eventType, count, conditions } = event;
  const parsedConditions = parseConditionsFromApi(conditions, customerProperties);
  const parsedCount = count ? parseCountFromApi(count) : null;

  return {
    type,
    event: eventType,
    count: parsedCount,
    conditions: parsedConditions,
  };
}

export function parseCountFromApi(count: ListConditionOperator): ListConditionOperator {
  const { op, value } = count;
  let parsedOp = op;
  let parsedValue = Number(value);
  if (op === "$gte" && value === 1) {
    parsedOp = "$exists";
    parsedValue = 1;
  }

  return { op: parsedOp, value: parsedValue };
}
export function parseConditionsFromApi(
  conditions: ListCondition[],
  customerProperties?: CustomerProperties,
): ListCondition[] {
  return conditions.map((condition) => {
    const { key } = condition;
    const options = [];
    if (customerProperties) {
      options.push(...customerPropertyOptions(customerProperties).options);
    }
    const conditionKey = options.find((k) => k.value === key) as {
      condition?: any;
      type?: any;
      value?: string;
    };
    if (conditionKey?.condition) condition.condition = conditionKey.condition;
    // Resolve the effective property type. When property metadata exists, use
    // it. Otherwise (event nested conditions, unsampled keys) infer from the
    // saved operators so the date / boolean / number / string branches and the
    // round-trip back through parseConditionsForApi still work.
    const resolvedType: PropertyType =
      conditionKey?.type ?? inferTypeFromOperators(condition.operators ?? []);
    condition.type = resolvedType;

    // Normalize boolean primitives to the string form that BOOLEAN_VALUES uses.
    // parseConditionsForApi expects 'true' / 'false' strings and coerces them
    // back to real booleans on save, so the round-trip stays clean.
    if (resolvedType === "boolean") {
      condition.operators = condition.operators.map((operator) => {
        if (typeof operator.value === "boolean") {
          return { op: operator.op, value: String(operator.value) };
        }
        return operator;
      });
    }

    // Check if string operator is $exists and use boolean value
    if (resolvedType === "string") {
      const parsedOperators: ListConditionOperator[] = [];
      condition.operators.forEach((operator) => {
        const operatorOp: ListConditionOperatorOp = operator.op;
        let operatorValue: ListConditionOperatorValue = operator.value;

        if (operator.op === "$exists") {
          operatorValue = String(operatorValue === true);
        }
        parsedOperators.push({ op: operatorOp, value: operatorValue });
      });

      condition.operators = parsedOperators;
    } else if (resolvedType === "date") {
      const parsedOperators: ListConditionOperator[] = [];

      const collapsedExact = collapseExactDateOperatorsForForm(condition.operators);
      const operatorsForRelative = collapsedExact ?? condition.operators;

      // `$lt` is the exclusive upper bound seeded intro windows use; treat
      // it like `$lte` when detecting a "within next" pair. Empty `$now: {}`
      // is "now" (0 days).
      const hasNextOperator = operatorsForRelative.some((op) => {
        if (op.op !== "$lte" && op.op !== "$lt") return false;
        const bound = operatorNowBound(op);
        return bound != null && bound.amount >= 0;
      });

      const filteredOperators = hasNextOperator
        ? operatorsForRelative.filter((op) => {
            if (op.op !== "$gte") return true;
            const bound = operatorNowBound(op);
            return !(bound && bound.amount === 0);
          })
        : operatorsForRelative;

      filteredOperators.forEach((operator) => {
        let operatorOp: ListConditionOperatorOp = operator.op;
        let operatorValue: ListConditionOperatorValue = operator.value;
        const bound = operatorNowBound(operator);

        if (bound) {
          if (operator.op === "$gte" && bound.amount < 0) {
            operatorOp = "$last";
            operatorValue = {
              $now: {
                duration_unit: bound.unit,
                duration_value: String(Math.abs(bound.amount)),
              },
            };
          } else if ((operator.op === "$lte" || operator.op === "$lt") && bound.amount >= 0) {
            operatorOp = "$next";
            operatorValue = {
              $now: {
                duration_unit: bound.unit,
                duration_value: String(bound.amount),
              },
            };
          } else if ((operator.op === "$lte" || operator.op === "$lt") && bound.amount < 0) {
            // Dormancy: "more than N ago". Keep `$lte` and rewrite to the
            // editor's duration shape so the chip is not an empty calendar.
            operatorValue = {
              $now: {
                duration_unit: bound.unit,
                duration_value: String(Math.abs(bound.amount)),
              },
            };
          }
        }

        parsedOperators.push({ op: operatorOp, value: operatorValue });
      });

      condition.operators = parsedOperators;
    } else if (ORDINAL_PROPERTIES.has((conditionKey?.value ?? key) as string)) {
      const parsedOperators: ListConditionOperator[] = [];

      const hasLte = condition.operators.some((op) => op.op === "$lte");
      const hasGte = condition.operators.some((op) => op.op === "$gte");

      if (hasGte) {
        const operatorValue = condition.operators.find((op) => op.op === "$gte")?.value;
        parsedOperators.push({
          op: "$last",
          value: { $gte: { $ordinal: operatorValue?.$ordinal * -1 } },
        });
      } else if (hasLte) {
        const operatorValue = condition.operators.find((op) => op.op === "$lte")?.value;
        parsedOperators.push({
          op: "$next",
          value: { $lte: { $ordinal: operatorValue?.$ordinal } },
        });
      } else {
        condition.operators.forEach((operator) => {
          let operatorOp: ListConditionOperatorOp = operator.op;
          let operatorValue: ListConditionOperatorValue = operator.value;

          if (operator.value.$ordinal) {
            if (operator.op === "$gte") {
              operatorOp = "$next";
              operatorValue = { $lte: { $ordinal: operator.value.$ordinal } };
            } else if (operator.op === "$lte") {
              operatorOp = "$last";
              operatorValue = { $gte: { $ordinal: operator.value.$ordinal } };
            }
          }

          parsedOperators.push({ op: operatorOp, value: operatorValue });
        });
      }

      condition.operators = parsedOperators;
    }

    return condition;
  });
}

// formatHslColor function
export function formatHslColor(hslString: string): HslColor {
  const [h, s, l] = hslString.split(" ").map((value, index) => {
    // Convert percentage to decimal for 's' and 'l'
    return index > 0 ? Number.parseFloat(value) / 100 : Number.parseInt(value, 10);
  });

  return { h, s, l };
}

// parseHslColor function
export function parseHslColor(hslColor: HslColor): string {
  const { h, s, l } = hslColor;
  // Convert 's' and 'l' from decimal to percentage
  return `${h} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`;
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
}

export function parseOrdinalNumber(number: any): string {
  const _number = Number(number);
  function ordinal(n: number) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
  return ordinal(_number);
}

export const COLOR_SCALE = [
  "#03045eff",
  "#023e8aff",
  "#0077b6ff",
  "#0096c7ff",
  "#00b4d8ff",
  "#48cae4ff",
  "#90e0efff",
  "#ade8f4ff",
  "#caf0f8ff",
];

interface Category {
  title: string;
}
interface Article {
  title: string;
  slug: {
    current: string;
  };
  categories?: Category[];
}
type GroupedArticles = {
  [category: string]: Partial<Article>[];
};
export function groupArticlesByCategory(articles: Article[]): GroupedArticles {
  return articles.reduce((grouped: GroupedArticles, article: Partial<Article>) => {
    article.categories?.forEach((category) => {
      const categoryTitle = category.title;
      if (!grouped[categoryTitle]) {
        grouped[categoryTitle] = [];
      }
      grouped[categoryTitle].push(article);
    });
    return grouped;
  }, {});
}

export function isDateWithinInterval(
  date: Date | string,
  interval: { duration: number; unit: keyof Duration },
): boolean {
  const dateTime = DateTime.fromJSDate(new Date(date));
  const now = DateTime.now();
  const durationAgo = now.minus({ [interval.unit]: interval.duration });

  return dateTime >= durationAgo;
}

export function formatDateRelative(
  input: Date | string,
  options: { specificity?: "detailed" | "luxon"; withTime?: boolean | "at" } = {},
): string {
  const now = DateTime.now();
  const inputDate = DateTime.fromJSDate(new Date(input));

  // Use Luxon's built-in relative formatting (e.g., "2 days ago", "in 3 hours")
  if (options?.specificity === "luxon") {
    return inputDate.toRelative() || inputDate.toFormat("MMM d, yyyy");
  }

  // Calendar days, not a rolling 24-hour window. Yesterday at 4:45 PM must
  // not render as "4:45 PM" the next morning (that reads as later today).
  const dayDiff = Math.round(now.startOf("day").diff(inputDate.startOf("day"), "days").days);
  // Today's label is already the time; `withTime` appends it to older labels
  // (e.g. "Yesterday, 2:03 PM", "Feb 27, 3:18 PM") for surfaces like the
  // call log where the time of day always matters. `"at"` uses
  // "Tuesday at 2:15 PM" for form and page activity.
  const time = inputDate.toFormat("h:mm a");

  if (dayDiff <= 0) {
    return time;
  }

  let label: string;
  if (dayDiff === 1) {
    label = "Yesterday";
  } else if (dayDiff < 7) {
    label = inputDate.toFormat("EEEE");
  } else if (now.year === inputDate.year) {
    label = inputDate.toFormat("MMM d");
  } else {
    label = inputDate.toFormat("M/d/yyyy");
  }
  if (!options?.withTime) return label;
  return options.withTime === "at" ? `${label} at ${time}` : `${label}, ${time}`;
}

/**
 * Date bucket for list grouping: Today, Yesterday, This week, Last week,
 * then month ("May", or "May 2023" for previous years).
 */
export function relativeDateSection(input: Date | string): string {
  const date = DateTime.fromJSDate(new Date(input));
  const now = DateTime.now();
  if (date.hasSame(now, "day")) return "Today";
  if (date.hasSame(now.minus({ days: 1 }), "day")) return "Yesterday";
  if (date >= now.startOf("week")) return "This week";
  if (date >= now.startOf("week").minus({ weeks: 1 })) return "Last week";
  return date.year === now.year ? date.toFormat("MMMM") : date.toFormat("MMMM yyyy");
}

/**
 * Compact relative label for a scheduled (future) send: relative day
 * ("Today" / "Tomorrow" / "Yesterday" / weekday) when close, otherwise a short
 * "M/D/YY, h:mm a" stamp. Unlike {@link formatDateRelative} this keeps the
 * time-of-day, which matters for scheduling, and reads correctly for both past
 * and near-future dates.
 */
export function formatScheduleLabel(date: Date | string): string {
  const dt = DateTime.fromJSDate(new Date(date));
  const time = dt.toFormat("h:mm a");
  const diffDays = Math.round(dt.startOf("day").diff(DateTime.now().startOf("day"), "days").days);
  if (diffDays === 0) return `Today, ${time}`;
  if (diffDays === 1) return `Tomorrow, ${time}`;
  if (diffDays === -1) return `Yesterday, ${time}`;
  if (diffDays > 1 && diffDays < 7) return `${dt.toFormat("ccc")}, ${time}`;
  return dt.toFormat("M/d/yy, h:mm a");
}

export function getAutomationConditionOperatorName(op: AutomationConditionOperator["op"]): string {
  return STRING_OPERATORS.find((operator) => operator.value === op)?.label || "";
}

export function insertContentIntoTextarea({
  name,
  textareaRef,
  setValue,
  content,
}: {
  name: string;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  setValue: (name: string, value: string) => void;
  content: string;
}) {
  const textarea = textareaRef.current;
  if (!textarea) return;

  const selectionStart = textarea.selectionStart;
  const selectionEnd = textarea.selectionEnd;
  const newValue =
    textarea.value.slice(0, selectionStart) + content + textarea.value.slice(selectionEnd);

  setValue(name, newValue);

  // Calculate new cursor position
  const newCursorPosition = selectionStart + content.length;
  // Focus the textarea and set cursor to new position
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
  }, 0);
}

/**
 * regular expression to check for valid hour format (01-23)
 */
export function isValidHour(value: string) {
  return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
}

/**
 * regular expression to check for valid 12 hour format (01-12)
 */
export function isValid12Hour(value: string) {
  return /^(0[1-9]|1[0-2])$/.test(value);
}

/**
 * regular expression to check for valid minute format (00-59)
 */
export function isValidMinuteOrSecond(value: string) {
  return /^[0-5][0-9]$/.test(value);
}

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean };

export function getValidNumber(
  value: string,
  { max, min = 0, loop = false }: GetValidNumberConfig,
) {
  let numericValue = Number.parseInt(value, 10);

  if (!Number.isNaN(numericValue)) {
    if (!loop) {
      if (numericValue > max) numericValue = max;
      if (numericValue < min) numericValue = min;
    } else {
      if (numericValue > max) numericValue = min;
      if (numericValue < min) numericValue = max;
    }
    return numericValue.toString().padStart(2, "0");
  }

  return "00";
}

export function getValidHour(value: string) {
  if (isValidHour(value)) return value;
  return getValidNumber(value, { max: 23 });
}

export function getValid12Hour(value: string) {
  if (isValid12Hour(value)) return value;
  return getValidNumber(value, { min: 1, max: 12 });
}

export function getValidMinuteOrSecond(value: string) {
  if (isValidMinuteOrSecond(value)) return value;
  return getValidNumber(value, { max: 59 });
}

type GetValidArrowNumberConfig = {
  min: number;
  max: number;
  step: number;
};

export function getValidArrowNumber(value: string, { min, max, step }: GetValidArrowNumberConfig) {
  let numericValue = Number.parseInt(value, 10);
  if (!Number.isNaN(numericValue)) {
    numericValue += step;
    return getValidNumber(String(numericValue), { min, max, loop: true });
  }
  return "00";
}

export function getValidArrowHour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 23, step });
}

export function getValidArrow12Hour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 1, max: 12, step });
}

export function getValidArrowMinuteOrSecond(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 59, step });
}

export function setMinutes(date: Date, value: string) {
  const minutes = getValidMinuteOrSecond(value);
  date.setMinutes(Number.parseInt(minutes, 10));
  return date;
}

export function setSeconds(date: Date, value: string) {
  const seconds = getValidMinuteOrSecond(value);
  date.setSeconds(Number.parseInt(seconds, 10));
  return date;
}

export function setHours(date: Date, value: string) {
  const hours = getValidHour(value);
  date.setHours(Number.parseInt(hours, 10));
  return date;
}

export function set12Hours(date: Date, value: string, period: Period) {
  const hours = Number.parseInt(getValid12Hour(value), 10);
  const convertedHours = convert12HourTo24Hour(hours, period);
  date.setHours(convertedHours);
  return date;
}

export type TimePickerType = "minutes" | "seconds" | "hours" | "12hours";
export type Period = "AM" | "PM";

export function setDateByType(date: Date, value: string, type: TimePickerType, period?: Period) {
  switch (type) {
    case "minutes":
      return setMinutes(date, value);
    case "seconds":
      return setSeconds(date, value);
    case "hours":
      return setHours(date, value);
    case "12hours": {
      if (!period) return date;
      return set12Hours(date, value, period);
    }
    default:
      return date;
  }
}

export function getDateByType(date: Date, type: TimePickerType) {
  switch (type) {
    case "minutes":
      return getValidMinuteOrSecond(String(date.getMinutes()));
    case "seconds":
      return getValidMinuteOrSecond(String(date.getSeconds()));
    case "hours":
      return getValidHour(String(date.getHours()));
    case "12hours": {
      const hours = display12HourValue(date.getHours());
      return getValid12Hour(String(hours));
    }
    default:
      return "00";
  }
}

export function getArrowByType(value: string, step: number, type: TimePickerType) {
  switch (type) {
    case "minutes":
      return getValidArrowMinuteOrSecond(value, step);
    case "seconds":
      return getValidArrowMinuteOrSecond(value, step);
    case "hours":
      return getValidArrowHour(value, step);
    case "12hours":
      return getValidArrow12Hour(value, step);
    default:
      return "00";
  }
}

/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
export function convert12HourTo24Hour(hour: number, period: Period) {
  if (period === "PM") {
    if (hour <= 11) {
      return hour + 12;
    } else {
      return hour;
    }
  } else if (period === "AM") {
    if (hour === 12) return 0;
    return hour;
  }
  return hour;
}

/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
export function display12HourValue(hours: number) {
  if (hours === 0 || hours === 12) return "12";
  if (hours >= 22) return `${hours - 12}`;
  if (hours % 12 > 9) return `${hours}`;
  return `0${hours % 12}`;
}

/**
 * Starter and Pro match on type and billing interval alone, so subscriptions
 * on a grandfathered platform rate still resolve to their list plan.
 */
export function findCurrentPlan(subscription: Subscription | undefined, plans: SubscriptionPlan[]) {
  if (!subscription) return null;

  if (subscription.configuration.type === "starter" || subscription.configuration.type === "pro") {
    return plans.find(
      (plan) =>
        plan.configuration.type === subscription.configuration.type &&
        plan.configuration.billingInterval === subscription.configuration.billingInterval,
    );
  }

  const currentPlan = plans.find(
    (plan) =>
      plan.configuration.type === subscription.configuration.type &&
      plan.configuration.billingInterval === subscription.configuration.billingInterval &&
      plan.configuration.platformRate === subscription.configuration.platformRate &&
      plan.configuration.segmentRate === subscription.configuration.segmentRate &&
      plan.configuration.segmentCredit === subscription.configuration.segmentCredit &&
      plan.configuration.inboxLimit === subscription.configuration.inboxLimit,
  );
  return currentPlan;
}

export function removeSystemPrefixFromString(text: string) {
  return text.replace(/^\[\*\]\s+/, "");
}
